<script lang="ts" setup generic="T extends string | number">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import Sortable from 'sortablejs'
import { Assert } from '@previo/core'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import PIcon from '../icon/PIcon.vue'
import { PDragAndDropListVariant } from './types'

type Props = {
  variant?: PDragAndDropListVariant
  disable?: boolean
}

const model = defineModel<T[]>()

const props = withDefaults(defineProps<Props>(), {
  variant: PDragAndDropListVariant.ROW,
  disable: false,
})

const listContainer = ref<HTMLElement | null>(null)

onMounted(() => {
  if (!listContainer.value) {
    return
  }

  Assert.instanceOf(listContainer.value, HTMLElement)

  const sortable = new Sortable(listContainer.value, {
    animation: 150,
    ghostClass: 'ds-drag-item--ghost',
    dragClass: 'ds-drag-item--dragging',
    forceFallback: true,
    fallbackOnBody: true,
    disabled: props.disable,
    handle: props.variant === PDragAndDropListVariant.ROW ? '.ds-drag-item__handle' : undefined,
    filter: 'a, button, input',
    preventOnFilter: false,
    onEnd: (event) => {
      if (model.value && event.oldIndex !== undefined && event.newIndex !== undefined) {
        const updatedItems = [...model.value]
        const [draggedItem] = updatedItems.splice(event.oldIndex, 1)

        updatedItems.splice(event.newIndex, 0, draggedItem)

        model.value = updatedItems
      }
    },
  })

  watch(
    () => props.disable,
    (disable) => sortable.option('disabled', disable),
  )

  onUnmounted(() => sortable.destroy())
})
</script>

<template>
  <div
    ref="listContainer"
    :class="[
      props.variant === PDragAndDropListVariant.CARD
        ? 'ds-drag-list--card'
        : 'p-display-flex p-flex-direction-column p-no-wrap',
    ]"
  >
    <div
      v-for="itemKey in model"
      :key="itemKey"
      class="ds-drag-item"
      :class="{
        'ds-drag-item--card': props.variant === PDragAndDropListVariant.CARD,
        'p-cursor-grab': props.variant === PDragAndDropListVariant.CARD && !props.disable,
      }"
    >
      <template v-if="$slots[`item.${itemKey}`]">
        <!-- Card variant: slot fills the card, grip floats as a bottom-center overlay -->
        <template v-if="props.variant === PDragAndDropListVariant.CARD">
          <slot :name="`item.${itemKey}`" />

          <div
            v-if="!props.disable"
            data-cy="drag-handle"
            class="p-cursor-move ds-drag-item__handle--card"
          >
            <p-icon icon="grip-dots-vertical" />
          </div>
        </template>

        <!-- Row variant: grip handle on the left, content on the right -->
        <div v-else class="p-display-flex p-items-center p-no-wrap p-full-width">
          <div
            v-if="!props.disable"
            data-cy="drag-handle"
            class="p-cursor-move ds-drag-item__handle"
          >
            <p-space :mobile="PSpaceSize.SM" :desktop="PSpaceSize.SM" />
            <p-icon icon="grip-dots-vertical" />
            <p-space :mobile="PSpaceSize.SM" :desktop="PSpaceSize.SM" />
          </div>

          <div class="p-flex-1">
            <slot :name="`item.${itemKey}`" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/brand.module';
@import '../../assets/scss/shared/variables/ds.spacing';
@import '../../assets/scss/shared/variables/ds.radius';

.ds-drag-list--card {
  display: flex;
  flex-wrap: wrap;
  gap: $space-sm-desktop;
  border-radius: $radius-lg-mobile;
}

.ds-drag-item {
  position: relative;

  &--ghost {
    opacity: 0.4;
    background: getColor('primary-lighten-4');
  }

  &--dragging {
    opacity: 0.9;
    z-index: 1000;
    background: white;
  }

  &--card {
    display: flex;
    border-radius: $radius-lg-mobile;
  }

  &__handle {
    color: getColor('grey500');
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
  }

  // Card variant: grip handle overlaid at the bottom-center of the card.
  &__handle--card {
    position: absolute;
    bottom: $space-sm-desktop;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    color: getColor('grey500');
  }
}

// This class is applied by SortableJS to the item being chosen.
.sortable-chosen {
  cursor: grabbing;
}

// This class is applied by SortableJS to the fallback element.
.sortable-fallback {
  opacity: 0.9;
  z-index: 1000;
  background: white;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  transform: scale(1.02);
}
</style>
